import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeRadioGroupComponent } from '../../payee-radio/payee-radio-group/payee-radio-group.component';
import { TranslateModule } from '@ngx-translate/core';

type RecipientOption = { value: 'USER' | 'ATTORNEY'; label: string };

type DialogData = {
  transition_label: string;
};

@Component({
  selector: 'app-send-custom-message-dialog',
  templateUrl: './send-custom-message-dialog.component.html',
  styleUrls: ['./send-custom-message-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    PayeeButtonComponent,
    PayeeRadioGroupComponent,
    TranslateModule,
  ],
})
export class SendCustomMessageDialogComponent implements OnInit {
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<SendCustomMessageDialogComponent>>(MatDialogRef);
  private workflowService = inject(WorkflowService);
  private fb = inject(FormBuilder);

  loading = false;
  dialogData?: DialogData;

  readonly recipientOptions: RecipientOption[] = [
    { value: 'USER', label: 'Hitelező' },
    { value: 'ATTORNEY', label: 'Ügyvéd' },
  ];

  readonly form = this.fb.group({
    recipient: this.fb.control(this.recipientOptions[0], {
      validators: Validators.required,
      nonNullable: true,
    }),
    sendAsTask: this.fb.control<boolean | null>(null, {
      validators: Validators.required,
    }),
    message: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(10000)],
      nonNullable: true,
    }),
  });

  readonly recipientDisplayWith = (opt?: RecipientOption): string =>
    opt?.label ?? '';

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;
  }

  sendAsTaskOptions(): boolean[] {
    return [true, false];
  }

  sendAsTaskLabel(value: boolean): string {
    const recipientDative =
      this.form.controls.recipient.value?.value === 'ATTORNEY'
        ? 'Ügyvédnek'
        : 'Hitelezőnek';
    return value
      ? `Teendőként elküldöm a ${recipientDative}`
      : `Nem küldem el teendőként a ${recipientDative}`;
  }

  readonly sendAsTaskDisplayWith = (value: boolean): string =>
    this.sendAsTaskLabel(value);

  readonly onSubmit = async () => {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.loading) {
      return;
    }

    const { recipient, message, sendAsTask } = this.form.getRawValue();
    if (sendAsTask === null) {
      return;
    }

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        recipient: recipient.value,
        message,
        send_as_task: sendAsTask,
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };
}
